import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ScrollText, X, Calendar, ArrowRight } from "lucide-react";
import MarkdownArticle from "../components/tavern/MarkdownArticle";

const CATEGORY_LABELS = {
  "notice-board": "Notice Board",
  "adventures": "Adventures",
  "radio": "Nocturne Radio",
  "second-life": "Second Life",
  "star-charts": "Star Charts",
  "life-updates": "Life Updates",
};

// Posts shown on the Notice Board page (everything except the standalone
// Star Charts curiosity cabinet & Second Life journal which have their own rooms).
const BOARD_CATEGORIES = ["notice-board", "adventures", "radio", "life-updates"];

const CATEGORY_FILTERS = [
  { key: "", label: "All Notices" },
  { key: "notice-board", label: "Updates" },
  { key: "adventures", label: "Adventures" },
  { key: "radio", label: "Radio" },
  { key: "life-updates", label: "Life" },
];

function formatDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return ""; }
}

// If the image URL points at one of our generated derivatives, return the
// thumbnail variant for the card grid. External URLs are returned unchanged.
function thumbVariant(url) {
  if (!url) return url;
  return url.replace(/-web\.webp(\?.*)?$/i, "-thumb.webp$1");
}

export default function NoticeBoard() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all(BOARD_CATEGORIES.map(c => api.get("/posts", { params: { category: c } })))
      .then(results => {
        const merged = results.flatMap(r => r.data)
          .sort((x, y) => new Date(y.created_at) - new Date(x.created_at));
        setItems(merged);
      }).catch(() => {});
  }, []);

  // Deep-link support: /notice-board#post=<id>
  useEffect(() => {
    if (!items.length) return;
    const m = (location.hash || "").match(/post=([^&]+)/);
    if (m) {
      const p = items.find(i => i.id === m[1]);
      if (p) setOpen(p);
    }
  }, [items, location.hash]);

  const filtered = useMemo(() =>
    filter ? items.filter(i => i.category === filter) : items,
  [items, filter]);

  const openPost = (post) => {
    setOpen(post);
    navigate(`#post=${post.id}`, { replace: false });
  };
  const closePost = () => {
    setOpen(null);
    navigate(window.location.pathname, { replace: true });
  };

  return (
    <div className="page-container fade-in" data-testid="notice-board-page">
      <div className="page-eyebrow">announcements & updates</div>
      <h1 className="page-title">Tavern Notice Board</h1>
      <p className="page-sub">
        The tavern keeps flexible hours. Watch the Notice Board for upcoming adventures, project updates, and news from
        around the hearth. Some weeks are bustling, some are quiet, but there is always another story waiting around the corner.
      </p>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mt-8" data-testid="notice-filters">
        {CATEGORY_FILTERS.map(c => (
          <button
            key={c.key}
            className="gold-btn"
            onClick={() => setFilter(c.key)}
            data-testid={`notice-filter-${c.key || "all"}`}
            style={{
              background: filter === c.key ? "var(--gold)" : "transparent",
              color: filter === c.key ? "var(--bg-base)" : "var(--gold)",
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Card grid */}
      <div className="bento-grid mt-8" data-testid="notice-board">
        {filtered.map(p => (
          <article key={p.id} className="room-card overflow-hidden flex flex-col" data-testid={`notice-card-${p.id}`}>
            {p.image_url && (
              <button onClick={() => openPost(p)} className="block" aria-label={`Open ${p.title}`}>
                <img src={thumbVariant(p.image_url)} alt={p.title} className="w-full aspect-[16/10] object-cover" loading="lazy" />
              </button>
            )}
            <div className="p-5 flex flex-col flex-1">
              <div className="flex items-center gap-2 text-xs">
                <span className="page-eyebrow" style={{ fontSize: "0.65rem" }}>
                  {CATEGORY_LABELS[p.category] || p.category}
                </span>
                <span style={{ color: "var(--muted)" }}>·</span>
                <span className="flex items-center gap-1 text-xs" style={{ color: "var(--muted)" }}>
                  <Calendar className="h-3 w-3" /> {formatDate(p.created_at)}
                </span>
              </div>
              <h3 className="tavern-heading text-2xl gold-text mt-2 leading-tight">{p.title}</h3>
              <p className="text-sm mt-3 flex-1" style={{ color: "var(--muted)" }}>{p.excerpt}</p>
              <button
                onClick={() => openPost(p)}
                className="gold-btn self-start mt-5 text-xs"
                data-testid={`read-more-${p.id}`}
              >
                Read More <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </article>
        ))}
        {!filtered.length && (
          <div className="col-span-full text-center py-12" style={{ color: "var(--muted)" }}>
            <ScrollText className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--gold)" }} />
            The board is freshly cleaned — new notices arrive soon.
          </div>
        )}
      </div>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 grid place-items-start overflow-y-auto p-4 md:p-8"
             style={{ background: "rgba(0,0,0,0.78)" }}
             onClick={closePost}
             data-testid="post-modal">
          <article className="parchment-card max-w-3xl w-full mx-auto p-7 md:p-12 relative"
                   onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-3 right-3 p-2 rounded-sm hover:bg-[rgba(212,175,55,0.1)]"
              onClick={closePost}
              aria-label="Close"
              data-testid="post-modal-close">
              <X style={{ color: "var(--gold)" }} />
            </button>

            {open.image_url && (
              <img
                src={open.image_url}
                alt={open.title}
                className="w-full max-h-[420px] object-cover rounded-sm mb-6"
                style={{ border: "1px solid rgba(212,175,55,0.25)" }}
                data-testid="post-featured-image"
              />
            )}

            <div className="flex items-center gap-3 text-xs flex-wrap">
              <span className="page-eyebrow">{CATEGORY_LABELS[open.category] || open.category}</span>
              <span style={{ color: "var(--muted)" }}>·</span>
              <span className="flex items-center gap-1" style={{ color: "var(--muted)" }}>
                <Calendar className="h-3 w-3" /> {formatDate(open.created_at)}
              </span>
              {(open.tags || []).map(t => (
                <span key={t} className="px-2 py-0.5 rounded-full text-[0.65rem]"
                      style={{ border: "1px solid rgba(212,175,55,0.3)", color: "var(--orchid-light)" }}>{t}</span>
              ))}
            </div>

            <h2 className="tavern-heading text-3xl md:text-4xl gold-text mt-3 leading-tight"
                data-testid="post-modal-title">{open.title}</h2>

            {open.excerpt && (
              <p className="tavern-script text-xl md:text-2xl mt-3" style={{ color: "var(--orchid-light)" }}>
                {open.excerpt}
              </p>
            )}

            <div className="gold-rule my-6" />

            <MarkdownArticle className="prose-wide" data-testid="post-modal-body">
              {open.body}
            </MarkdownArticle>
          </article>
        </div>
      )}
    </div>
  );
}
